/**
 * Which definition tooltip is open, and why (BEN-141 P28, #803).
 *
 * Radix's tooltip opens on hover and on focus and closes on Escape. It
 * deliberately does not open on click, because a real tooltip is a hover
 * affordance. That is the right default for a hint and the wrong one for a
 * definition: a phone has no hover, so twelve definitions that only a pointer can
 * reach do not exist for a touch user at all, and the figures they define (a debt
 * column, the Splash Fund) are exactly the ones #803 says are being misread.
 *
 * Adding a click path means owning the open state, and owning it raises two
 * questions the uncontrolled component answered for free. This module answers
 * them in one place, with no DOM, so both are testable:
 *
 * 1. **One at a time.** Opening any definition closes the one before it. Two
 *    floating panels over a table is worse than none.
 * 2. **A click outranks the pointer.** A tooltip opened by tapping is PINNED: it
 *    survives the pointer leaving, because on a touch screen "the pointer left"
 *    fires at the end of the tap that opened it, and on a desktop someone who
 *    clicked has said they want to read it. Escape, a second click, or opening
 *    another one closes it. Hover alone still opens and closes freely.
 */

import { useSyncExternalStore } from "react";

export interface DefinitionTooltipState {
  /** The open definition's id, or null. */
  openId: string | null;
  /** True when the open one was opened by a click rather than by hover or focus. */
  pinned: boolean;
}

export const CLOSED: DefinitionTooltipState = { openId: null, pinned: false };

/** A click on the trigger: open it pinned, or close it if it is already pinned open. */
export function toggle(state: DefinitionTooltipState, id: string): DefinitionTooltipState {
  if (state.openId === id && state.pinned) return CLOSED;
  return { openId: id, pinned: true };
}

/** Hover or focus arrived. It never steals from a pinned tooltip. */
export function hoverOpen(state: DefinitionTooltipState, id: string): DefinitionTooltipState {
  if (state.pinned && state.openId !== id) return state;
  if (state.openId === id) return state;
  return { openId: id, pinned: false };
}

/** Hover or focus left. A pinned tooltip stays; anything else closes. */
export function hoverClose(state: DefinitionTooltipState, id: string): DefinitionTooltipState {
  if (state.openId !== id || state.pinned) return state;
  return CLOSED;
}

/** Escape closes whatever is open, pinned or not. */
export function escape(): DefinitionTooltipState {
  return CLOSED;
}

export function isOpen(state: DefinitionTooltipState, id: string): boolean {
  return state.openId === id;
}

export type DefinitionTooltipEvent =
  | { type: "toggle"; id: string }
  | { type: "hoverOpen"; id: string }
  | { type: "hoverClose"; id: string }
  | { type: "escape" };

export function reduce(
  state: DefinitionTooltipState,
  event: DefinitionTooltipEvent,
): DefinitionTooltipState {
  switch (event.type) {
    case "toggle":
      return toggle(state, event.id);
    case "hoverOpen":
      return hoverOpen(state, event.id);
    case "hoverClose":
      return hoverClose(state, event.id);
    case "escape":
      return escape();
  }
}

/**
 * The state is app-wide rather than per-component because "one at a time" is a
 * statement about the page, and the twelve call sites sit in five components that
 * do not share a parent below the app shell.
 */
export function createDefinitionTooltipStore() {
  let state: DefinitionTooltipState = CLOSED;
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    dispatch(event: DefinitionTooltipEvent) {
      const next = reduce(state, event);
      if (next === state) return;
      state = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    /** Tests only: the store outlives a single render tree. */
    reset() {
      state = CLOSED;
      for (const listener of listeners) listener();
    },
  };
}

export const definitionTooltipStore = createDefinitionTooltipStore();

/**
 * The store's React binding, kept beside the store rather than at the one call
 * site that happens to need it.
 *
 * `useSyncExternalStore` is not optional for a module-level store under React 19:
 * without it two components reading this store in a single concurrent render can
 * paint different answers to "which definition is open", and the twelve triggers
 * are spread across five components precisely so that no parent re-renders them
 * together. The subscribe/getState pair is stable for the life of the module, so
 * this re-renders only the triggers whose own open state changed. The third
 * argument is the server snapshot: rendered on the server, nothing is open.
 */
export function useDefinitionTooltipState(): DefinitionTooltipState {
  return useSyncExternalStore(
    definitionTooltipStore.subscribe,
    definitionTooltipStore.getState,
    definitionTooltipStore.getState,
  );
}
